import React, { useState } from 'react';
import { FiUsers, FiMicOff, FiCameraOff, FiSquare, FiX, FiUserX, FiCheckCircle, FiSearch, FiEdit3 } from 'react-icons/fi';

const ParticipantsModal = ({ 
  showParticipantsModal, 
  setShowParticipantsModal, 
  participants, 
  activeSpeakers, 
  handleMuteViewerAudio, 
  handleStopViewerScreenShare,
  emitSocketEvent,
  sessionId,
  roomCode,
  addDebugLog,
  handleBanParticipant,
  handleWhiteboardPermission,
  bannedUsers = [],
  handleUnbanParticipant,
  allowedWhiteboardUsers = [] // 👈 Naya Prop
}) => {
  const [activeTab, setActiveTab] = useState('active'); // 'active' | 'banned'
  const [searchQuery, setSearchQuery] = useState(''); // 👈 Search state

  if (!showParticipantsModal) return null;

  // 🔍 Filtering Logic
  const filteredParticipants = participants.filter(p => 
    (p.name || p.userName || p.userId || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredBanned = bannedUsers.filter(u => 
    (u.name || u.userId || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gray-800 rounded-xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col shadow-2xl border border-gray-700">
        
        {/* HEADER */}
        <div className="flex justify-between items-center p-5 border-b border-gray-700 shrink-0 bg-gray-800/90">
          <h3 className="text-xl font-bold text-white flex items-center gap-2">
            <FiUsers className="text-blue-400" />
            Participants Management
          </h3>
          <button 
            onClick={() => setShowParticipantsModal(false)}
            className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
          >
            <FiX className="h-6 w-6" />
          </button>
        </div>

        {/* TABS & SEARCH */}
        <div className="p-5 shrink-0 bg-gray-800 space-y-4 shadow-sm z-10">
          
          <div className="flex border-b border-gray-700">
            <button
              onClick={() => setActiveTab('active')}
              className={`px-6 py-3 font-medium text-sm transition-all relative ${
                activeTab === 'active' 
                  ? 'text-blue-400' 
                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/50'
              }`}
            >
              Active ({participants.length})
              {activeTab === 'active' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-blue-400 rounded-t-md"></div>}
            </button>
            <button
              onClick={() => setActiveTab('banned')}
              className={`px-6 py-3 font-medium text-sm transition-all relative ${
                activeTab === 'banned' 
                  ? 'text-red-400' 
                  : 'text-gray-400 hover:text-gray-300 hover:bg-gray-700/50'
              }`}
            >
              Banned ({bannedUsers.length})
              {activeTab === 'banned' && <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-red-400 rounded-t-md"></div>}
            </button>
          </div>

          {/* 🔍 SEARCH BAR */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="text-gray-400 h-5 w-5" />
            </div>
            <input
              type="text"
              placeholder={`Search in ${activeTab} participants...`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-gray-900 text-white border border-gray-600 rounded-lg pl-10 pr-4 py-2.5 focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all placeholder-gray-500"
            />
            {searchQuery && (
              <button 
                onClick={() => setSearchQuery('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-white"
              >
                <FiX />
              </button>
            )}
          </div>
        </div>
        
        {/* LIST CONTAINER */}
        <div className="flex-1 overflow-y-auto p-5 space-y-3 bg-gray-900/50">
          
          {/* ACTIVE TAB CONTENT */}
          {activeTab === 'active' && filteredParticipants.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400">
              <FiUsers className="h-12 w-12 mb-3 opacity-20" />
              <p>{searchQuery ? "No matching participants found" : "No active participants"}</p>
            </div>
          )}

          {activeTab === 'active' && filteredParticipants.map((participant) => {
            // ✏️ Draw Permission Logic
            const canDraw = allowedWhiteboardUsers.includes(participant.userId);

            return (
              <div 
                key={participant.userId || participant.socketId} 
                className="flex items-center justify-between p-3.5 bg-gray-800 border border-gray-700 hover:border-gray-600 rounded-xl transition-colors"
              >
                <div className="flex items-center space-x-4 flex-1 min-w-0 pr-4">
                  <div className="relative flex-shrink-0">
                    <div className="w-10 h-10 rounded-full bg-gray-700 flex items-center justify-center text-gray-300 font-bold uppercase border border-gray-600">
                      {(participant.name || participant.userName || 'U')[0]}
                    </div>
                    <div 
                      className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-gray-800 ${
                        activeSpeakers.has(participant.socketId) 
                          ? 'bg-green-500 animate-pulse' 
                          : participant.hasAudio 
                            ? 'bg-blue-500' 
                            : 'bg-gray-500'
                      }`}
                    ></div>
                  </div>

                  <div className="flex flex-col min-w-0">
                    <span className="truncate text-white font-medium text-sm">
                      {participant.name || participant.userName || participant.userId || 'User'}
                    </span>
                    <div className="flex gap-2 mt-1">
                      {participant.isScreenSharing && <span className="bg-yellow-500/20 text-yellow-400 text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1"><FiSquare className="w-3 h-3"/> Screen</span>}
                      {participant.hasAudio && <span className="bg-blue-500/20 text-blue-400 text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">🎤 Audio</span>}
                      {participant.hasVideo && <span className="bg-green-500/20 text-green-400 text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1">📹 Video</span>}
                      {canDraw && <span className="bg-indigo-500/20 text-indigo-400 text-[10px] px-1.5 py-0.5 rounded flex items-center gap-1"><FiEdit3 className="w-3 h-3"/> Drawing</span>}
                    </div>
                  </div>
                </div>
                
                <div className="flex flex-wrap gap-2 justify-end shrink-0">
                  {/* ✏️ CONDITIONAL WHITEBOARD BUTTON */}
                  {canDraw ? (
                    <button onClick={() => handleWhiteboardPermission(participant.userId, false)} className="bg-orange-500/10 hover:bg-orange-500/20 border border-orange-500/30 text-orange-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">Revoke Draw</button>
                  ) : (
                    <button onClick={() => handleWhiteboardPermission(participant.userId, true)} className="bg-indigo-500/10 hover:bg-indigo-500/20 border border-indigo-500/30 text-indigo-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors">Allow Draw</button>
                  )}

                  {participant.hasAudio && (
                    <button onClick={() => handleMuteViewerAudio(participant.socketId)} className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-2.5 py-1.5 rounded-lg transition-colors" title="Mute Mic"><FiMicOff className="w-4 h-4" /></button>
                  )}
                  {participant.isScreenSharing && (
                    <button onClick={() => handleStopViewerScreenShare(participant.userId)} className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-2.5 py-1.5 rounded-lg transition-colors" title="Stop Screen Share"><FiSquare className="w-4 h-4" /></button>
                  )}
                  {participant.hasVideo && (
                    <button onClick={() => {
                        emitSocketEvent("streamer-stop-viewer-video", { sessionId: sessionId || roomCode, targetSocketId: participant.socketId });
                        addDebugLog(`🛑 Force stopped camera for: ${participant.userId}`);
                      }} 
                      className="bg-gray-700 hover:bg-gray-600 text-gray-300 px-2.5 py-1.5 rounded-lg transition-colors" title="Stop Camera"><FiCameraOff className="w-4 h-4" /></button>
                  )}

                  {/* BAN BUTTON */}
                  <button
                    onClick={() => {
                      const name = participant.name || participant.userName || 'this user';
                      if(window.confirm(`Are you sure you want to ban ${name}?`)) {
                        handleBanParticipant(participant.userId, name);
                      }
                    }}
                    className="flex items-center gap-1.5 bg-red-500/10 hover:bg-red-500/20 border border-red-500/30 text-red-400 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ml-1"
                    title="Ban Participant"
                  >
                    <FiUserX className="w-3.5 h-3.5" />
                    <span>Ban</span>
                  </button>
                </div>
              </div>
            );
          })}

          {/* BANNED TAB CONTENT */}
          {activeTab === 'banned' && filteredBanned.length === 0 && (
            <div className="flex flex-col items-center justify-center py-10 text-gray-400">
              <FiUserX className="h-12 w-12 mb-3 opacity-20 text-red-400" />
              <p>{searchQuery ? "No matching banned users found" : "No banned users"}</p>
            </div>
          )}

          {activeTab === 'banned' && filteredBanned.map((user) => (
            <div 
              key={user.userId} 
              className="flex items-center justify-between p-4 bg-red-900/10 border border-red-900/30 rounded-xl"
            >
              <div className="flex items-center space-x-4">
                <div className="w-10 h-10 rounded-full bg-red-500/20 flex items-center justify-center text-red-400">
                  <FiUserX className="h-5 w-5" />
                </div>
                <div className="flex flex-col">
                  <span className="truncate max-w-[250px] text-gray-200 font-medium">
                    {user.name || user.userId}
                  </span>
                  <span className="text-[10px] text-gray-500 uppercase tracking-wider mt-0.5">Banned User</span>
                </div>
              </div>
              
              <button
                onClick={() => {
                  if(window.confirm(`Unban ${user.name || 'this user'}?`)) {
                    handleUnbanParticipant(user.userId);
                  }
                }}
                className="flex items-center gap-2 bg-green-500/10 hover:bg-green-500/20 border border-green-500/30 text-green-400 px-4 py-2 rounded-lg text-sm font-medium transition-colors"
              >
                <FiCheckCircle className="w-4 h-4" />
                <span>Unban User</span>
              </button>
            </div>
          ))}

        </div>
      </div>
    </div>
  );
};

export default ParticipantsModal;


// import React, { useState } from 'react';
// import { FiUsers, FiMicOff, FiCameraOff, FiSquare, FiX, FiUserX, FiCheckCircle } from 'react-icons/fi';

// const ParticipantsModal = ({ 
//   showParticipantsModal, 
//   setShowParticipantsModal, 
//   participants, 
//   activeSpeakers, 
//   handleMuteViewerAudio, 
//   handleStopViewerScreenShare,
//   emitSocketEvent,
//   sessionId,
//   roomCode,
//   addDebugLog,
//   handleBanParticipant,
//   handleWhiteboardPermission,
//   bannedUsers = [], // 👈 Naya prop
//   handleUnbanParticipant // 👈 Naya prop
// }) => {
//   const [activeTab, setActiveTab] = useState('active'); // 'active' | 'banned'

//   if (!showParticipantsModal) return null;

//   return (
//     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
//       <div className="bg-gray-800 rounded-lg p-6 w-[36rem] max-h-[80vh] overflow-hidden flex flex-col">
//         {/* HEADER */}
//         <div className="flex justify-between items-center mb-4 shrink-0">
//           <h3 className="text-lg font-semibold text-white">Participants Management</h3>
//           <button 
//             onClick={() => setShowParticipantsModal(false)}
//             className="text-gray-400 hover:text-white"
//           >
//             <FiX className="h-6 w-6" />
//           </button>
//         </div>

//         {/* TABS */}
//         <div className="flex border-b border-gray-700 mb-4 shrink-0">
//           <button
//             onClick={() => setActiveTab('active')}
//             className={`px-4 py-2 font-medium text-sm transition-colors ${
//               activeTab === 'active' 
//                 ? 'text-blue-400 border-b-2 border-blue-400' 
//                 : 'text-gray-400 hover:text-gray-300'
//             }`}
//           >
//             Active ({participants.length})
//           </button>
//           <button
//             onClick={() => setActiveTab('banned')}
//             className={`px-4 py-2 font-medium text-sm transition-colors ${
//               activeTab === 'banned' 
//                 ? 'text-red-400 border-b-2 border-red-400' 
//                 : 'text-gray-400 hover:text-gray-300'
//             }`}
//           >
//             Banned ({bannedUsers.length})
//           </button>
//         </div>
        
//         {/* LIST CONTAINER */}
//         <div className="space-y-3 overflow-y-auto flex-1 pr-2">
          
//           {/* ACTIVE TAB CONTENT */}
//           {activeTab === 'active' && participants.length === 0 && (
//             <p className="text-gray-400 text-center py-4">No active participants</p>
//           )}
//           {activeTab === 'active' && participants.map((participant) => (
//             <div 
//               key={participant.userId || participant.socketId} 
//               className="flex items-center justify-between p-3 bg-gray-700 rounded-lg"
//             >
//               <div className="flex items-center space-x-3">
//                 <div 
//                   className={`w-3 h-3 rounded-full ${
//                     activeSpeakers.has(participant.socketId) 
//                       ? 'bg-green-500' 
//                       : participant.hasAudio 
//                         ? 'bg-blue-500' 
//                         : 'bg-gray-500'
//                   }`}
//                 ></div>
//                 <span className="truncate max-w-[150px] text-white font-medium">
//                   {participant.name || participant.userName || participant.userId || 'User'}
//                 </span>
//               </div>
              
//               <div className="flex flex-wrap gap-2 justify-end items-center">
//                 {participant.isScreenSharing && <span className="bg-yellow-500 text-xs px-2 py-1 rounded text-white" title="Screen Sharing">🖥️</span>}
//                 {participant.hasAudio && <span className="bg-blue-500 text-xs px-2 py-1 rounded text-white" title="Has Audio">🎤</span>}
//                 {activeSpeakers.has(participant.socketId) && <span className="bg-green-500 text-xs px-2 py-1 rounded text-white" title="Speaking">🔊</span>}

//                 <button onClick={() => handleWhiteboardPermission(participant.userId, true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-2 py-1 rounded text-xs transition-colors">Allow Draw</button>
//                 <button onClick={() => handleWhiteboardPermission(participant.userId, false)} className="bg-orange-600 hover:bg-orange-700 text-white px-2 py-1 rounded text-xs transition-colors">Revoke Draw</button>

//                 {participant.hasAudio && (
//                   <button onClick={() => handleMuteViewerAudio(participant.socketId)} className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs transition-colors">Mute</button>
//                 )}
//                 {participant.isScreenSharing && (
//                   <button onClick={() => handleStopViewerScreenShare(participant.userId)} className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs transition-colors">Stop Share</button>
//                 )}
//                 {participant.hasVideo && (
//                   <button onClick={() => {
//                       emitSocketEvent("streamer-stop-viewer-video", { sessionId: sessionId || roomCode, targetSocketId: participant.socketId });
//                       addDebugLog(`🛑 Force stopped camera for: ${participant.userId}`);
//                     }} 
//                     className="bg-red-600 hover:bg-red-700 text-white px-2 py-1 rounded text-xs transition-colors">Stop Cam</button>
//                 )}

//                 {/* BAN BUTTON */}
//                 <button
//                   onClick={() => {
//                     const name = participant.name || participant.userName || 'this user';
//                     if(window.confirm(`Are you sure you want to ban ${name}? They won't be able to rejoin.`)) {
//                       handleBanParticipant(participant.userId, name);
//                     }
//                   }}
//                   className="flex items-center space-x-1 bg-red-900/80 hover:bg-red-800 text-red-100 px-2 py-1 rounded text-xs transition-colors border border-red-700"
//                   title="Ban Participant"
//                 >
//                   <FiUserX className="w-3 h-3" />
//                   <span>Ban</span>
//                 </button>
//               </div>
//             </div>
//           ))}

//           {/* BANNED TAB CONTENT */}
//           {activeTab === 'banned' && bannedUsers.length === 0 && (
//             <p className="text-gray-400 text-center py-4">No banned users</p>
//           )}
//           {activeTab === 'banned' && bannedUsers.map((user) => (
//             <div 
//               key={user.userId} 
//               className="flex items-center justify-between p-3 bg-red-900/20 border border-red-900/50 rounded-lg"
//             >
//               <div className="flex items-center space-x-3">
//                 <FiUserX className="h-4 w-4 text-red-500" />
//                 <span className="truncate max-w-[200px] text-gray-300 font-medium">
//                   {user.name || user.userId}
//                 </span>
//               </div>
              
//               <button
//                 onClick={() => {
//                   if(window.confirm(`Unban ${user.name || 'this user'}? They will be able to join again.`)) {
//                     handleUnbanParticipant(user.userId);
//                   }
//                 }}
//                 className="flex items-center space-x-1 bg-green-600/80 hover:bg-green-500 text-white px-3 py-1.5 rounded text-xs transition-colors"
//               >
//                 <FiCheckCircle className="w-4 h-4" />
//                 <span>Unban</span>
//               </button>
//             </div>
//           ))}

//         </div>
//       </div>
//     </div>
//   );
// };

// export default ParticipantsModal;


